import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ReservationRow, WriteReceipt } from '@smile/shared';
import { ApiError, api } from '../api/client';
import { splitDt } from '../lib/filters';
import { CZK } from '../lib/format';

const kc = (n: number) => `${CZK.format(n)} Kč`;

/**
 * The departure strip — the legacy's, with one change the owner asked for.
 *
 * Shown on a reservation that is on site (or booked and never stamped in —
 * the yard has those too) and not yet stamped out. Opened, it is the old
 * strip: what is owed, the amount being taken (prefilled with what is
 * owed), hotově or kartou, the KeyCafe box the key came back from, and two
 * buttons. **"Zaplaceno" takes the money and nothing else** — in the old
 * system it also closed the booking, so a customer who paid at the desk
 * dropped into Odjelo before the car was handed over and no proper
 * check-out could follow (the owner, 20 October 2026: "a bug from the old
 * system we need to fix"). After the payment the row stays, owes nothing,
 * and the second button — "Odjezd" (or "Odjezd, předplaceno" when it was
 * paid online or by the agency) — closes it when the car has gone.
 *
 * Not optimistic, like ArriveButton: the row changes when the database
 * says so.
 */
export function DepartButton({ r, compact, defaultOpen }: { r: ReservationRow; compact?: boolean; defaultOpen?: boolean }) {
  const qc = useQueryClient();
  /* `defaultOpen`: the strip is shown at once, as the old board showed it
     the moment a row was opened — the dispatcher, 20 October 2026: "so
     where is the payment possibility?" (it was behind a button). */
  const [open, setOpen] = useState(defaultOpen ?? false);
  const [amount, setAmount] = useState(String(Math.max(0, Math.round(r.outstanding))));
  const [method, setMethod] = useState<'cash' | 'card'>('cash');
  const [keycafe, setKeycafe] = useState(r.keycafeDeparture ?? '');
  const [receipt, setReceipt] = useState<WriteReceipt | null>(null);

  const eligible = ['new', 'pending', 'paid', 'verified'].includes(r.status) && !r.departureReal;
  const owed = Math.max(0, Math.round(r.outstanding));

  const [paidNow, setPaidNow] = useState<WriteReceipt | null>(null);

  /* Zaplaceno: the payment alone. The board refetches; `outstanding` comes
     back 0 and the close button appears. */
  const pay = useMutation({
    mutationFn: () => api.pay(r.id, { amount: Math.round(Number(amount)), method }),
    onSuccess: async (rc) => {
      setPaidNow(rc);
      await qc.invalidateQueries();
    },
  });

  /* Odjezd: the close, never with money — that is the other button. */
  const m = useMutation({
    mutationFn: () => api.depart(r.id, { ...(keycafe.trim() ? { keycafeDeparture: keycafe.trim() } : {}) }),
    onSuccess: async (rc) => {
      setReceipt(rc);
      setOpen(false);
      await qc.invalidateQueries();
    },
  });

  if (receipt) {
    return (
      <span className="arrived-mark" title={`Označeno jako odjeté ${receipt.at}`}>
        ✔ Odjel v {splitDt(receipt.at).t}{' '}
        {/* The legacy printed the receipt right after the close; here it is
            one click, in a new tab that prints itself. */}
        <a className="btn sm" href={`/uctenka/${r.id}`} target="_blank" rel="noopener" onClick={(e) => e.stopPropagation()}>
          Účtenka
        </a>
      </span>
    );
  }
  if (!eligible) return null;

  const err = pay.error ?? m.error;
  const message =
    err instanceof ApiError
      ? err.serverMessage ??
        (err.code === 'writes_disabled'
          ? 'Zápisy jsou na tomto serveru vypnuté.'
          : err.code === 'forbidden'
            ? 'Na tuhle akci nemáte oprávnění.'
            : err.message)
      : err instanceof Error
        ? err.message
        : null;

  if (!open) {
    return (
      <button
        type="button"
        className={compact ? 'btn sm primary' : 'btn primary'}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        Odjezd
      </button>
    );
  }

  const n = Number(amount);
  const amountOk = Number.isInteger(n) && n > 0;
  const busy = pay.isPending || m.isPending;
  // "předplaceno" is the legacy's word for paid online or by the agency; a
  // payment taken at this desk is not that.
  const prepaid = owed === 0 && r.paidOnSite === 0 && r.paid > 0;

  return (
    <form
      className="arrive-form depart-form"
      onClick={(e) => e.stopPropagation()}
      onSubmit={(e) => {
        e.preventDefault();
        if (!busy && amountOk && owed > 0) pay.mutate();
      }}
    >
      <span className="depart-owed">
        Zaplatit: <strong>{kc(owed)}</strong>
        {(r.paid > 0 || r.servicesPrice > 0 || r.penalty > 0) && (
          <span className="was">
            {' '}
            (parkování {kc(r.price)}
            {r.servicesPrice > 0 ? ` + služby ${kc(r.servicesPrice)}` : ''}
            {r.penalty > 0 ? ` + pozdní odjezd ${kc(r.penalty)}` : ''}
            {r.paid > 0 ? `, uhrazeno ${kc(r.paid)}` : ''})
          </span>
        )}
      </span>
      <label>
        Platba
        <input
          type="number"
          inputMode="numeric"
          min={1}
          step={1}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="Kč"
          autoFocus
        />
      </label>
      <label>
        Způsob
        <select value={method} onChange={(e) => setMethod(e.target.value as 'cash' | 'card')}>
          <option value="cash">Hotově</option>
          <option value="card">Kartou</option>
        </select>
      </label>
      <label>
        KeyCafe kód odjezd
        <input
          value={keycafe}
          onChange={(e) => setKeycafe(e.target.value)}
          placeholder="kód boxu"
          maxLength={8}
          pattern="[A-Za-z0-9-]{1,8}"
        />
      </label>
      {/* The legacy departure strip's two buttons (the dispatcher's
          screenshots, 20 October 2026) — "Zaplaceno" while money is owed,
          "Odjezd" once it is not. Hotově is the first choice, as there. */}
      {owed > 0 && (
        <button type="submit" className="btn primary" disabled={busy || !amountOk}>
          {pay.isPending ? 'Ukládám…' : 'Zaplaceno'}
        </button>
      )}
      {paidNow && owed === 0 && (
        <span className="arrived-mark" title={`Platba přijata ${paidNow.at}`}>
          ✔ Zaplaceno v {splitDt(paidNow.at).t}
        </span>
      )}
      {owed === 0 && (
        <button type="button" className="btn warn" onClick={() => m.mutate()} disabled={busy}>
          {m.isPending ? 'Ukládám…' : prepaid ? 'Odjezd, předplaceno' : 'Odjezd'}
        </button>
      )}
      {!defaultOpen && (
        <button type="button" className="btn" onClick={() => setOpen(false)} disabled={busy}>
          Zrušit
        </button>
      )}
      {message && <span className="arrive-error">{message}</span>}
    </form>
  );
}
